/**
 * Meetings module (Întâlniri) — API client. Mirrors backend/src/routes/meetingLog.ts
 * and the serializer in backend/src/lib/meetings/meetingLog.ts.
 *
 * A meeting's transcript/notes are served by the polymorphic meeting-notes API
 * (`meetingNotesApi.ts`, owner `{ meetingId }`); this client covers the meeting
 * card itself and the external-contact directory.
 */
import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { IngestStatus, MeetingType } from './meetingNotesApi';

interface Envelope<T> {
  success: true;
  data: T;
}

export interface PageMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// --- Types ------------------------------------------------------------------------

export interface MeetingContact {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  companyName: string | null;
  role: string | null;
  notes: string | null;
  clientId: string | null;
  client: { id: string; name: string } | null;
  companyId: string | null;
  createdAt: string;
  updatedAt: string;
  /** Present on directory listings only. */
  meetingsCount?: number;
}

export interface MeetingContactDetail extends MeetingContact {
  meetings: {
    id: string;
    title: string;
    startAt: string;
    owner: { id: string; name: string };
    client: { id: string; name: string } | null;
    project: { id: string; name: string; code: string | null } | null;
    lead: { id: string; identifier: string; title: string } | null;
  }[];
}

/** The lead a meeting belongs to, as the meeting card shows it. */
export interface MeetingLeadRef {
  id: string;
  identifier: string;
  title: string;
  companyName: string | null;
  contactName: string | null;
}

export interface MeetingNoteSummary {
  id: string;
  title: string;
  date: string;
  source: string;
  ingestStatus: IngestStatus | string;
  summary: string | null;
  decisions: string | null;
  actionItems: string | null;
  keyFacts: string | null;
  openQuestions: string | null;
  updatedAt: string;
}

export interface MeetingTeamMember {
  id: string;
  fullName: string;
  email: string | null;
  userId: string | null;
  avatarUrl: string | null;
}

export type MeetingAccessLevel = 'VIEW' | 'EDIT';

/** A colleague who may open the meeting without having been in it. */
export interface MeetingAccessEntry {
  userId: string;
  name: string;
  avatarUrl: string | null;
  level: MeetingAccessLevel;
}

export interface Meeting {
  id: string;
  title: string;
  startAt: string;
  endAt: string | null;
  location: string | null;
  meetingType: MeetingType | string;
  notes: string | null;
  companyId: string | null;
  company: { id: string; name: string } | null;
  owner: { id: string; name: string; email: string; avatarUrl: string | null };
  createdBy: { id: string; name: string } | null;
  client: { id: string; name: string } | null;
  project: { id: string; name: string; code: string | null } | null;
  lead: MeetingLeadRef | null;
  attendees: MeetingContact[];
  teamMembers: MeetingTeamMember[];
  access: MeetingAccessEntry[];
  /** Detail read only: false when the viewer has view-only access. */
  viewerCanEdit?: boolean;
  notesCount: number;
  latestNote: MeetingNoteSummary | null;
  meetingNotes: MeetingNoteSummary[];
  createdAt: string;
  updatedAt: string;
}

/** Either an existing contact or a new one created inline. */
export type AttendeeInput =
  | { contactId: string }
  | {
      firstName: string;
      lastName?: string | null;
      email?: string | null;
      phone?: string | null;
      companyName?: string | null;
      role?: string | null;
      clientId?: string | null;
    };

export interface MeetingInput {
  title: string;
  /** ISO instant. */
  startAt: string;
  endAt?: string | null;
  location?: string | null;
  meetingType?: MeetingType | string;
  ownerId?: string;
  clientId?: string | null;
  projectId?: string | null;
  leadId?: string | null;
  notes?: string | null;
  companyId?: string | null;
  attendees?: AttendeeInput[];
  teamMemberIds?: string[];
  /** Create only: transcript from the recording device (-> note + ingest). */
  transcript?: string | null;
}

export type MeetingSort = 'startAt' | '-startAt' | 'title' | '-title';

export interface MeetingFilters {
  search?: string;
  clientId?: string;
  projectId?: string;
  leadId?: string;
  ownerId?: string;
  contactId?: string;
  personId?: string;
  meetingType?: string;
  /** ISO date (yyyy-mm-dd) bounds, inclusive. */
  from?: string;
  to?: string;
  companyId?: string;
  sort?: MeetingSort;
  page?: number;
  limit?: number;
}

export interface MeetingContactInput {
  firstName: string;
  lastName?: string;
  email?: string | null;
  phone?: string | null;
  companyName?: string | null;
  role?: string | null;
  notes?: string | null;
  clientId?: string | null;
  companyId?: string | null;
}

export interface ContactFilters {
  search?: string;
  clientId?: string;
  companyId?: string;
  page?: number;
  limit?: number;
}

function qs(params: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

// --- Meetings -------------------------------------------------------------------------

export async function listMeetings(
  filters: MeetingFilters = {},
): Promise<{ data: Meeting[]; meta: PageMeta }> {
  const res = await apiFetch<Envelope<Meeting[]> & { meta: PageMeta }>(
    `/api/meeting-log${qs({
      search: filters.search,
      clientId: filters.clientId,
      projectId: filters.projectId,
      leadId: filters.leadId,
      ownerId: filters.ownerId,
      contactId: filters.contactId,
      personId: filters.personId,
      meetingType: filters.meetingType,
      from: filters.from,
      to: filters.to ? `${filters.to}T23:59:59.999` : undefined,
      companyId: filters.companyId,
      sort: filters.sort,
      page: filters.page,
      limit: filters.limit,
    })}`,
    { headers: authHeaders() },
  );
  // Test Mode's generic fallback has no rows — read defensively.
  return {
    data: Array.isArray(res.data) ? res.data : [],
    meta: res.meta ?? { page: 1, limit: filters.limit ?? 20, total: 0, totalPages: 1 },
  };
}

export async function getMeeting(id: string): Promise<Meeting> {
  const res = await apiFetch<Envelope<Meeting>>(`/api/meeting-log/${encodeURIComponent(id)}`, {
    headers: authHeaders(),
  });
  return res.data;
}

/** "Cere acces" on a meeting the caller holds a link to but is not part of. */
export async function requestMeetingAccess(
  id: string,
): Promise<{ ownerName: string; alreadyHasAccess: boolean }> {
  const res = await apiFetch<Envelope<{ ownerName: string; alreadyHasAccess: boolean }>>(
    `/api/meeting-log/${encodeURIComponent(id)}/access-request`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}

/** Let a colleague view or edit the meeting, or change their level. */
export async function grantMeetingAccess(
  id: string,
  userId: string,
  level: MeetingAccessLevel,
): Promise<Meeting> {
  const res = await apiFetch<Envelope<Meeting>>(
    `/api/meeting-log/${encodeURIComponent(id)}/grant-access`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({ userId, level }) },
  );
  return res.data;
}

export async function revokeMeetingAccess(id: string, userId: string): Promise<Meeting> {
  const res = await apiFetch<Envelope<Meeting>>(
    `/api/meeting-log/${encodeURIComponent(id)}/access/${encodeURIComponent(userId)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}

export async function createMeeting(input: MeetingInput): Promise<Meeting> {
  const res = await apiFetch<Envelope<Meeting>>('/api/meeting-log', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateMeeting(
  id: string,
  patch: Partial<Omit<MeetingInput, 'transcript'>>,
): Promise<Meeting> {
  const res = await apiFetch<Envelope<Meeting>>(`/api/meeting-log/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(patch),
  });
  return res.data;
}

export async function deleteMeeting(id: string): Promise<void> {
  await apiFetch<Envelope<{ id: string; deleted: boolean }>>(
    `/api/meeting-log/${encodeURIComponent(id)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

// --- Task suggestions ------------------------------------------------------------------
// Tasks the AI proposes after reading the transcript. Nothing is created until a
// person accepts a card (which creates the Task and links it) or dismisses it.

export type TaskSuggestionStatus = 'PENDING' | 'ACCEPTED' | 'DISMISSED';
export type TaskSuggestionPriority = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';

export interface MeetingTaskSuggestion {
  id: string;
  /** Exactly one owner is set: the meeting, or the voice-room call. */
  meetingId: string | null;
  callId?: string | null;
  noteId: string | null;
  title: string;
  description: string | null;
  /** What was said in the meeting that motivates this task. */
  reason: string | null;
  priority: TaskSuggestionPriority;
  /** yyyy-mm-dd */
  dueDate: string | null;
  project: { id: string; name: string; code: string | null } | null;
  assignee: { id: string; name: string } | null;
  /** The person the AI named when no team member matched. */
  assigneeHint: string | null;
  projectHint: string | null;
  status: TaskSuggestionStatus;
  task: { id: string; identifier: string | null; title: string } | null;
  decidedBy: { id: string; name: string } | null;
  decidedAt: string | null;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export interface AcceptTaskSuggestionInput {
  title?: string;
  description?: string | null;
  projectId: string;
  assigneeId?: string | null;
  priority?: TaskSuggestionPriority;
  /** yyyy-mm-dd or null. */
  dueDate?: string | null;
  /** Effort in hours, as on the task. */
  estimate?: number | null;
}

function suggestionsUrl(meetingId: string, tail = ''): string {
  return `/api/meeting-log/${encodeURIComponent(meetingId)}/task-suggestions${tail}`;
}

export async function listTaskSuggestions(meetingId: string): Promise<MeetingTaskSuggestion[]> {
  const res = await apiFetch<Envelope<MeetingTaskSuggestion[]>>(suggestionsUrl(meetingId), {
    headers: authHeaders(),
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function generateTaskSuggestions(
  meetingId: string,
  noteId?: string,
): Promise<MeetingTaskSuggestion[]> {
  const res = await apiFetch<Envelope<MeetingTaskSuggestion[]>>(
    suggestionsUrl(meetingId, '/generate'),
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(noteId ? { noteId } : {}),
    },
  );
  return Array.isArray(res.data) ? res.data : [];
}

export async function acceptTaskSuggestion(
  meetingId: string,
  suggestionId: string,
  input: AcceptTaskSuggestionInput,
): Promise<MeetingTaskSuggestion> {
  const res = await apiFetch<Envelope<MeetingTaskSuggestion>>(
    suggestionsUrl(meetingId, `/${encodeURIComponent(suggestionId)}/accept`),
    { method: 'POST', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

export async function dismissTaskSuggestion(
  meetingId: string,
  suggestionId: string,
): Promise<MeetingTaskSuggestion> {
  const res = await apiFetch<Envelope<MeetingTaskSuggestion>>(
    suggestionsUrl(meetingId, `/${encodeURIComponent(suggestionId)}/dismiss`),
    { method: 'POST', headers: authHeaders(), body: '{}' },
  );
  return res.data;
}

export async function restoreTaskSuggestion(
  meetingId: string,
  suggestionId: string,
): Promise<MeetingTaskSuggestion> {
  const res = await apiFetch<Envelope<MeetingTaskSuggestion>>(
    suggestionsUrl(meetingId, `/${encodeURIComponent(suggestionId)}/restore`),
    { method: 'POST', headers: authHeaders(), body: '{}' },
  );
  return res.data;
}

// --- Minutes ----------------------------------------------------------------------

export interface MinutesBullet {
  lead: string | null;
  text: string;
}

export interface MinutesAction {
  action: string;
  owner: string;
  dueDate: string | null;
}

export interface MeetingMinutesContent {
  title: string;
  subject: string;
  meetingTypeLabel: string;
  organizer: string;
  externalParticipants: string[];
  summary: string[];
  topics: Array<{
    heading: string;
    intro: string | null;
    bullets: MinutesBullet[];
    outro: string | null;
  }>;
  keyFacts: MinutesBullet[];
  decisions: string[];
  actions: MinutesAction[];
  openQuestions: string[];
  nextSteps: string;
}

export interface MeetingMinutes {
  id: string;
  /** The meeting this belongs to; null for the minutes of a voice-room call. */
  meetingId: string | null;
  /** The voice-room call this belongs to; null for a meeting's minutes. */
  callId: string | null;
  content: MeetingMinutesContent;
  model: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  costEur: number;
  createdById: string | null;
  createdAt: string;
  fileName: string;
  /** Link to the Word file in Google Drive, when it was saved there. */
  driveUrl: string | null;
  emailedAt: string | null;
  emailedTo: string[];
}

function minutesUrl(meetingId: string, tail = ''): string {
  return `/api/meeting-log/${encodeURIComponent(meetingId)}/minutes${tail}`;
}

export async function listMeetingMinutes(meetingId: string): Promise<MeetingMinutes[]> {
  const res = await apiFetch<Envelope<MeetingMinutes[]>>(minutesUrl(meetingId), {
    headers: authHeaders(),
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function generateMeetingMinutes(meetingId: string): Promise<MeetingMinutes> {
  const res = await apiFetch<Envelope<MeetingMinutes>>(minutesUrl(meetingId, '/generate'), {
    method: 'POST',
    headers: authHeaders(),
    body: '{}',
  });
  return res.data;
}

/** File an older minutes document in Drive (new ones go there on generation). */
export async function saveMeetingMinutesToDrive(
  meetingId: string,
  minutes: MeetingMinutes,
): Promise<MeetingMinutes> {
  const res = await apiFetch<Envelope<MeetingMinutes>>(
    minutesUrl(meetingId, `/${encodeURIComponent(minutes.id)}/save-to-drive`),
    { method: 'POST', headers: authHeaders(), body: '{}' },
  );
  return res.data;
}

/** Email the document to the participants. */
export async function emailMeetingMinutes(
  meetingId: string,
  minutes: MeetingMinutes,
): Promise<MeetingMinutes> {
  const res = await apiFetch<Envelope<MeetingMinutes>>(
    minutesUrl(meetingId, `/${encodeURIComponent(minutes.id)}/email`),
    { method: 'POST', headers: authHeaders(), body: '{}' },
  );
  return res.data;
}

/**
 * Fetch a minutes Word file with the app's Bearer token and hand it to the
 * browser. Shared with the voice-room call minutes, which live under another
 * URL but are the same document.
 */
export async function downloadMinutesDocx(url: string, fileName: string): Promise<void> {
  const res = await fetch(url, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Descărcarea a eșuat (${res.status})`);
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  a.rel = 'noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

export async function downloadMeetingMinutesDocx(
  meetingId: string,
  minutes: MeetingMinutes,
): Promise<void> {
  return downloadMinutesDocx(
    minutesUrl(meetingId, `/${encodeURIComponent(minutes.id)}/docx`),
    minutes.fileName,
  );
}

export async function attachMeetingTranscript(
  id: string,
  transcript: string,
): Promise<{ meetingId: string; noteId: string; duplicate: boolean }> {
  const res = await apiFetch<Envelope<{ meetingId: string; noteId: string; duplicate: boolean }>>(
    `/api/meeting-log/${encodeURIComponent(id)}/transcript`,
    { method: 'POST', headers: authHeaders(), body: JSON.stringify({ transcript }) },
  );
  return res.data;
}

export async function syncMeetingKb(id: string): Promise<{ id: string; kbPath: string | null }> {
  const res = await apiFetch<Envelope<{ id: string; kbPath: string | null }>>(
    `/api/meeting-log/${encodeURIComponent(id)}/sync-kb`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}

// --- Contacts -------------------------------------------------------------------------

export async function listMeetingContacts(
  filters: ContactFilters = {},
): Promise<{ data: MeetingContact[]; meta: PageMeta }> {
  const res = await apiFetch<Envelope<MeetingContact[]> & { meta: PageMeta }>(
    `/api/meeting-log/contacts${qs({
      search: filters.search,
      clientId: filters.clientId,
      companyId: filters.companyId,
      page: filters.page,
      limit: filters.limit,
    })}`,
    { headers: authHeaders() },
  );
  return {
    data: Array.isArray(res.data) ? res.data : [],
    meta: res.meta ?? { page: 1, limit: filters.limit ?? 50, total: 0, totalPages: 1 },
  };
}

export async function getMeetingContact(id: string): Promise<MeetingContactDetail> {
  const res = await apiFetch<Envelope<MeetingContactDetail>>(
    `/api/meeting-log/contacts/${encodeURIComponent(id)}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function createMeetingContact(input: MeetingContactInput): Promise<MeetingContact> {
  const res = await apiFetch<Envelope<MeetingContact>>('/api/meeting-log/contacts', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateMeetingContact(
  id: string,
  patch: Partial<MeetingContactInput>,
): Promise<MeetingContact> {
  const res = await apiFetch<Envelope<MeetingContact>>(
    `/api/meeting-log/contacts/${encodeURIComponent(id)}`,
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify(patch) },
  );
  return res.data;
}

export async function deleteMeetingContact(id: string): Promise<void> {
  await apiFetch<Envelope<{ id: string; deleted: boolean }>>(
    `/api/meeting-log/contacts/${encodeURIComponent(id)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

// --- Display helpers ---------------------------------------------------------------------

export const MEETING_TYPE_LABELS: Record<string, string> = {
  GENERAL: 'General',
  DISCOVERY: 'Discovery',
  STATUS: 'Status',
  PLANNING: 'Planificare',
  RETROSPECTIVE: 'Retrospectivă',
  DEMO: 'Demo',
  NEGOTIATION: 'Negociere',
  ONBOARDING: 'Onboarding',
};

/** The backend enum (Prisma MeetingType) — the notes API lists a wider set. */
export const MEETING_TYPE_OPTIONS = Object.keys(MEETING_TYPE_LABELS);

export function meetingTypeLabel(t: string): string {
  return MEETING_TYPE_LABELS[t] ?? t.charAt(0) + t.slice(1).toLowerCase();
}
